import React, {useState, useCallback, useEffect, useRef} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {Calculator, Beaker, DollarSign, Droplets, ChevronDown, Save} from 'lucide-react';
import {toast} from 'react-toastify';
import {AdminService} from '@/modules/admin/services/adminService';
import {usePricingCalculator, TIER_DEFAULT_MARKUP, getAddonPrice, type Multipliers} from '@/modules/provider-console/hooks/usePricingCalculator';
import type {PartnerTier, AssayCatalogItem, PricingSettings} from '@/shared/types/api';

const TIER_LABELS: Record<PartnerTier, string> = {
    payg: 'PAYG (Pay As You Go)',
    standard: 'Standard Wallet',
    preferred: 'Preferred Wallet',
    enterprise: 'Enterprise',
};

function fmt(val: number): string {
    return val.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function num(val: string | null | undefined): number {
    if (val == null) return 0;
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : 0;
}

interface LabTestPricingProps {
    orgId?: number;
}

export default function LabTestPricing({orgId}: LabTestPricingProps = {}) {
    const queryClient = useQueryClient();

    const [includedCodes, setIncludedCodes] = useState<Set<string>>(new Set());
    const [selectedTier, setSelectedTier] = useState<PartnerTier>('standard');
    const [multipliers, setMultipliers] = useState<Multipliers>({reagent: 1, calibrator: 1, shared: 1});
    const [markupOverride, setMarkupOverride] = useState<number | null>(null);
    const [memberDiscountPct, setMemberDiscountPct] = useState<number | null>(null);
    const [loadedPanel, setLoadedPanel] = useState<{id: number; name: string} | null>(null);
    const [panelName, setPanelName] = useState('');
    const settingsInitialized = useRef(false);
    const skipAutoSave = useRef(false);

    // Reset initialization flag when the org changes so we re-sync from DB
    useEffect(() => {
        settingsInitialized.current = false;
    }, [orgId]);

    const {data: assays = [], isLoading: assaysLoading} = useQuery({
        queryKey: ['admin', 'assay-catalog'],
        queryFn: () => AdminService.getAssayCatalog(),
        staleTime: 5 * 60 * 1000,
    });

    const {data: pricingSettingsData, isLoading: settingsLoading} = useQuery({
        queryKey: ['admin', 'pricing-settings', orgId],
        queryFn: () => AdminService.getPricingSettings(orgId!),
        staleTime: 5 * 60 * 1000,
        enabled: orgId != null,
    });

    const {data: orgPanels = []} = useQuery({
        queryKey: ['admin', 'pricing-panels', orgId],
        queryFn: () => AdminService.listPricingPanels(orgId!),
        enabled: orgId != null,
    });

    // Initialize state from DB — runs once per orgId when pricing settings load
    useEffect(() => {
        if (!pricingSettingsData || settingsInitialized.current) return;
        skipAutoSave.current = true;
        settingsInitialized.current = true;
        setSelectedTier(pricingSettingsData.partner_tier ?? 'standard');
        setMultipliers({
            reagent: pricingSettingsData.reagent_multiplier ?? 1,
            calibrator: pricingSettingsData.calibrator_multiplier ?? 1,
            shared: pricingSettingsData.shared_multiplier ?? 1,
        });
        setMarkupOverride(pricingSettingsData.markup_override);
        setMemberDiscountPct(pricingSettingsData.mylabs_plus_member_discount_pct);
    }, [pricingSettingsData]);

    // Auto-save pricing settings (debounced)
    const savePricingSettings = useMutation({
        mutationFn: (settings: PricingSettings) => AdminService.updatePricingSettings(orgId!, settings),
        onError: () => {
            toast.error('Failed to save pricing settings');
        },
    });

    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!orgId || !settingsInitialized.current) return;

        // Skip the auto-save triggered by the initialization effect setting state
        if (skipAutoSave.current) {
            skipAutoSave.current = false;
            return;
        }

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            savePricingSettings.mutate({
                partner_tier: selectedTier,
                reagent_multiplier: multipliers.reagent,
                calibrator_multiplier: multipliers.calibrator,
                shared_multiplier: multipliers.shared,
                markup_override: markupOverride,
                mylabs_plus_member_discount_pct: memberDiscountPct,
            });
        }, 500);

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTier, multipliers, markupOverride, memberDiscountPct]);

    const createPanelMutation = useMutation({
        mutationFn: ({name, testCodes}: {name: string; testCodes: string[]}) =>
            AdminService.createPricingPanel(orgId!, name, testCodes),
        onSuccess: (result, variables) => {
            queryClient.invalidateQueries({queryKey: ['admin', 'pricing-panels', orgId]});
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            setLoadedPanel({id: result.panelId, name: variables.name});
            toast.success(`Panel "${variables.name}" created`);
        },
        onError: () => {
            toast.error('Failed to create panel');
        },
    });

    const updatePanelMutation = useMutation({
        mutationFn: ({panelId, name, testCodes}: {panelId: number; name: string; testCodes: string[]}) =>
            AdminService.updatePricingPanel(orgId!, panelId, name, testCodes),
        onSuccess: (_result, variables) => {
            queryClient.invalidateQueries({queryKey: ['admin', 'pricing-panels', orgId]});
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            setLoadedPanel({id: variables.panelId, name: variables.name});
            toast.success(`Panel "${variables.name}" updated`);
        },
        onError: () => {
            toast.error('Failed to update panel');
        },
    });

    const activeMarkup = markupOverride ?? TIER_DEFAULT_MARKUP[selectedTier];
    const summary = usePricingCalculator(assays, includedCodes, selectedTier, multipliers, markupOverride);

    const toggleAssay = useCallback((code: string) => {
        setIncludedCodes((prev) => {
            const next = new Set(prev);
            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }
            return next;
        });
    }, []);

    const handleTierChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const tier = e.target.value as PartnerTier;
        setSelectedTier(tier);
        setMarkupOverride(null);
    }, []);

    const handleMarkupChange = useCallback((value: string) => {
        const n = parseFloat(value);
        if (!Number.isFinite(n) || n < 2) return;
        setMarkupOverride(n);
    }, []);

    const handleMultiplierChange = useCallback((field: keyof Multipliers, value: string) => {
        const n = parseFloat(value);
        if (!Number.isFinite(n) || n < 0) return;
        setMultipliers((prev) => ({...prev, [field]: n}));
    }, []);

    const selectAll = useCallback(() => {
        setIncludedCodes(new Set(assays.map((a) => a.code)));
    }, [assays]);

    const clearAll = useCallback(() => {
        setIncludedCodes(new Set());
    }, []);

    const handleLoadPanel = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const panelId = parseInt(e.target.value, 10);
            if (!panelId) {
                // "New Panel" selected
                setLoadedPanel(null);
                setPanelName('');
                setIncludedCodes(new Set());
                setMarkupOverride(null);
                return;
            }
            const panel = orgPanels.find((p) => p.id === panelId);
            if (!panel) return;

            const matched = new Set(panel.assay_codes);

            if (matched.size < panel.test_count && panel.test_count > 0) {
                toast.info(`Matched ${matched.size} of ${panel.test_count} tests from "${panel.name}"`);
            }

            setLoadedPanel({id: panel.id, name: panel.name});
            setPanelName(panel.name);
            setIncludedCodes(matched);
            setMarkupOverride(null);
        },
        [orgPanels]
    );

    const handleSaveAsNew = useCallback(() => {
        const trimmed = panelName.trim();
        if (!trimmed || includedCodes.size === 0) return;
        createPanelMutation.mutate({name: trimmed, testCodes: [...includedCodes]});
    }, [panelName, includedCodes, createPanelMutation]);

    const handleUpdatePanel = useCallback(() => {
        if (!loadedPanel) return;
        const trimmed = panelName.trim();
        if (!trimmed || includedCodes.size === 0) return;
        updatePanelMutation.mutate({panelId: loadedPanel.id, name: trimmed, testCodes: [...includedCodes]});
    }, [loadedPanel, panelName, includedCodes, updatePanelMutation]);

    const isSaving = createPanelMutation.isPending || updatePanelMutation.isPending;
    const canSave = panelName.trim().length > 0 && includedCodes.size > 0 && !isSaving;

    if (assaysLoading || settingsLoading) {
        return (
            <div className="rounded-xl border border-mld-panel-border bg-white p-6">
                <div className="flex items-center gap-2 text-mld-muted-text">
                    <Calculator className="h-5 w-5 animate-spin" />
                    <span>Loading pricing calculator...</span>
                </div>
            </div>
        );
    }

    const individualAssays = assays.filter((a) => !a.is_bundled_panel);
    const bundledPanels = assays.filter((a) => a.is_bundled_panel);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="rounded-xl border border-mld-panel-border bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-mld-admin-primary-50">
                            <Calculator className="h-5 w-5 text-mld-admin-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-mld-text-primary">Lab Test Pricing Calculator</h2>
                            <p className="text-sm text-mld-muted-text">
                                Select tests to build a custom panel and see real-time pricing
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Partner Tier */}
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-mld-text-secondary">Partner Tier:</label>
                            <div className="relative">
                                <select
                                    value={selectedTier}
                                    onChange={handleTierChange}
                                    className="appearance-none bg-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm font-medium text-mld-text-primary focus:border-mld-admin-primary focus:outline-none focus:ring-1 focus:ring-mld-admin-primary"
                                >
                                    <option value="payg">{TIER_LABELS.payg}</option>
                                    <option value="standard">{TIER_LABELS.standard}</option>
                                    <option value="preferred">{TIER_LABELS.preferred}</option>
                                    <option value="enterprise">{TIER_LABELS.enterprise}</option>
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-mld-muted-text" />
                            </div>
                        </div>

                        {/* Load Panel */}
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-mld-text-secondary">Load Panel:</label>
                            <div className="relative">
                                <select
                                    value={loadedPanel?.id ?? ''}
                                    onChange={handleLoadPanel}
                                    className="appearance-none bg-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm font-medium text-mld-text-primary focus:border-mld-admin-primary focus:outline-none focus:ring-1 focus:ring-mld-admin-primary"
                                >
                                    <option value="">— New Panel —</option>
                                    {orgPanels.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name} ({p.test_count} tests)
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-mld-muted-text" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cost Multipliers + Universal Markup (Enterprise only) */}
            {selectedTier === 'enterprise' && (
                <div className="rounded-xl border border-mld-panel-border bg-white p-4">
                    <div className="flex flex-wrap items-center gap-6">
                        <span className="text-sm font-medium text-mld-text-secondary">Cost Multipliers</span>
                        {(['reagent', 'calibrator', 'shared'] as const).map((field) => (
                            <div key={field} className="flex items-center gap-2">
                                <label className="text-xs text-mld-muted-text capitalize">{field}:</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={multipliers[field]}
                                    onChange={(e) => handleMultiplierChange(field, e.target.value)}
                                    className="w-16 rounded border border-mld-panel-border px-2 py-1 text-sm text-center focus:border-mld-admin-primary focus:outline-none focus:ring-1 focus:ring-mld-admin-primary"
                                />
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-mld-panel-border pt-3">
                        <span className="text-sm font-medium text-mld-text-secondary">Universal Markup</span>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                step="0.1"
                                min="2"
                                value={activeMarkup}
                                onChange={(e) => handleMarkupChange(e.target.value)}
                                className="w-16 rounded border border-mld-panel-border px-2 py-1 text-sm text-center focus:border-mld-admin-primary focus:outline-none focus:ring-1 focus:ring-mld-admin-primary"
                            />
                            <span className="text-xs text-mld-muted-text">×</span>
                        </div>
                        {markupOverride !== null && (
                            <button
                                onClick={() => setMarkupOverride(null)}
                                className="rounded px-2 py-0.5 text-xs font-medium text-mld-muted-text hover:bg-mld-menu-background"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* My Labs Plus Member Discount */}
            <div className="rounded-xl border border-mld-panel-border bg-white p-4">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-mld-text-primary">
                        My Labs Plus member discount
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min={0}
                            max={100}
                            step={0.5}
                            value={memberDiscountPct ?? ''}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') {
                                    setMemberDiscountPct(null);
                                    return;
                                }
                                const n = Number(v);
                                if (Number.isFinite(n)) setMemberDiscountPct(n);
                            }}
                            className="w-24 rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                        />
                        <span className="text-sm text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-500">
                        Discount applied to kit prices for My Labs Plus members. Blank = no discount.
                    </span>
                </div>
            </div>

            {/* Assay Catalog Table */}
            <div className="rounded-xl border border-mld-panel-border bg-white">
                <div className="flex items-center justify-between border-b border-mld-panel-border px-6 py-3">
                    <div className="flex items-center gap-2">
                        <Beaker className="h-4 w-4 text-mld-muted-text" />
                        <h3 className="text-sm font-semibold text-mld-text-primary">Test Catalog</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={selectAll}
                            className="rounded px-2 py-1 text-xs font-medium text-mld-admin-primary hover:bg-mld-admin-primary-50"
                        >
                            Select All
                        </button>
                        <button
                            onClick={clearAll}
                            className="rounded px-2 py-1 text-xs font-medium text-mld-muted-text hover:bg-mld-menu-background"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-mld-panel-border bg-mld-menu-background text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                <th className="px-6 py-2 w-10"></th>
                                <th className="px-3 py-2">Test Name</th>
                                <th className="px-3 py-2 text-center">Class</th>
                                <th className="px-3 py-2 text-right">
                                    <div className="leading-tight">Reagent +</div>
                                    <div className="leading-tight">Cal + Shared</div>
                                </th>
                                <th className="px-3 py-2 text-right">Panel Price</th>
                                <th className="px-3 py-2 text-right">Add-On</th>
                            </tr>
                        </thead>
                        <tbody>
                            {individualAssays.map((assay) => (
                                <AssayRow
                                    key={assay.code}
                                    assay={assay}
                                    isIncluded={includedCodes.has(assay.code)}
                                    onToggle={toggleAssay}
                                    markup={activeMarkup}
                                    tier={selectedTier}
                                    multipliers={multipliers}
                                />
                            ))}

                            {bundledPanels.length > 0 && (
                                <>
                                    <tr>
                                        <td colSpan={6} className="bg-mld-menu-background px-6 py-2 text-xs font-semibold uppercase tracking-wider text-mld-muted-text">
                                            Bundled Panels
                                        </td>
                                    </tr>
                                    {bundledPanels.map((assay) => (
                                        <AssayRow
                                            key={assay.code}
                                            assay={assay}
                                            isIncluded={includedCodes.has(assay.code)}
                                            onToggle={toggleAssay}
                                            markup={activeMarkup}
                                            tier={selectedTier}
                                            multipliers={multipliers}
                                        />
                                    ))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Panel Summary */}
            <div className="rounded-xl border border-mld-panel-border bg-white p-6">
                <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="h-4 w-4 text-mld-muted-text" />
                    <h3 className="text-sm font-semibold text-mld-text-primary">Panel Summary</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Pricing */}
                    <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                            <span className="text-mld-text-secondary">Tests Included</span>
                            <span className="font-medium text-mld-text-primary">
                                {summary.testsIncluded} of {summary.totalTests}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-mld-text-secondary">Panel Price</span>
                            <span className="font-semibold text-mld-admin-primary">${fmt(summary.panelPrice)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-mld-text-secondary">COGS</span>
                            <span className="font-medium text-mld-text-primary">${fmt(summary.cogs)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-mld-text-secondary">Gross Profit</span>
                            <span className="font-medium text-mld-text-primary">
                                ${fmt(summary.grossProfit)}
                                <span className="ml-2 text-xs text-mld-muted-text">
                                    {summary.marginPercent.toFixed(1)}% margin
                                </span>
                            </span>
                        </div>
                        <div className="border-t border-mld-panel-border pt-3 flex justify-between text-sm">
                            <span className="text-mld-text-secondary">Add-On Total</span>
                            <span className="font-semibold text-mld-text-primary">${fmt(summary.addonTotal)}</span>
                        </div>
                    </div>

                    {/* Serum Volume */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                            <Droplets className="h-4 w-4 text-blue-500" />
                            <span className="text-sm font-medium text-mld-text-secondary">Serum Volume</span>
                        </div>
                        <div className="text-sm text-mld-text-secondary space-y-1">
                            <div className="flex justify-between">
                                <span>IA (Dxi)</span>
                                <span className="font-mono text-xs text-mld-text-primary">
                                    {fmt(summary.serumIaUl)} + {summary.iaDeadSpace} dead = {fmt(summary.serumIaTotal)} µL
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span>Chem (Dxc)</span>
                                <span className="font-mono text-xs text-mld-text-primary">
                                    {fmt(summary.serumChemUl)} + {summary.chemDeadSpace} dead = {fmt(summary.serumChemTotal)} µL
                                </span>
                            </div>
                            <div className="border-t border-mld-panel-border pt-2 flex justify-between font-medium">
                                <span>Total</span>
                                <span className="font-mono text-xs text-mld-text-primary">{fmt(summary.serumTotal)} µL</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Save Panel */}
            <div className="rounded-xl border border-mld-panel-border bg-white p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Save className="h-4 w-4 text-mld-muted-text" />
                    <h3 className="text-sm font-semibold text-mld-text-primary">Save Panel</h3>
                </div>

                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-medium text-mld-text-secondary mb-1">Panel Name</label>
                        <input
                            type="text"
                            value={panelName}
                            onChange={(e) => setPanelName(e.target.value)}
                            placeholder="e.g. Women's Health Panel"
                            className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm text-mld-text-primary placeholder-gray-400 focus:border-mld-admin-primary focus:outline-none focus:ring-1 focus:ring-mld-admin-primary"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        {loadedPanel ? (
                            <>
                                <button
                                    onClick={handleUpdatePanel}
                                    disabled={!canSave}
                                    className="rounded-lg bg-mld-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-mld-admin-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {updatePanelMutation.isPending ? 'Updating...' : 'Update Panel'}
                                </button>
                                <button
                                    onClick={handleSaveAsNew}
                                    disabled={!canSave}
                                    className="rounded-lg border border-mld-panel-border bg-white px-4 py-2 text-sm font-medium text-mld-text-secondary hover:bg-mld-menu-background disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {createPanelMutation.isPending ? 'Saving...' : 'Save as New'}
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={handleSaveAsNew}
                                disabled={!canSave}
                                className="rounded-lg bg-mld-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-mld-admin-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {createPanelMutation.isPending ? 'Saving...' : 'Save as New Panel'}
                            </button>
                        )}
                    </div>
                </div>

                {includedCodes.size === 0 && (
                    <p className="mt-2 text-xs text-mld-muted-text">Select at least one test to save a panel.</p>
                )}
            </div>
        </div>
    );
}

function AssayRow({
    assay,
    isIncluded,
    onToggle,
    markup,
    tier,
    multipliers,
}: {
    assay: AssayCatalogItem;
    isIncluded: boolean;
    onToggle: (code: string) => void;
    markup: number;
    tier: PartnerTier;
    multipliers: Multipliers;
}) {
    const isCalc = assay.is_calculated;

    const reagent = num(assay.reagent_cost) * multipliers.reagent;
    const calibrator = num(assay.calibrator_cost) * multipliers.calibrator;
    const shared = num(assay.shared_cost) * multipliers.shared;
    const testCost = reagent + calibrator + shared;

    const testPrice = testCost * markup;

    const addon = getAddonPrice(assay, tier);

    const classLabel = assay.is_bundled_panel ? 'Panel' : assay.classification != null ? String(assay.classification) : '—';

    const freeBadge = (
        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
            Free
        </span>
    );

    return (
        <tr
            className={`border-b border-mld-panel-border/30 cursor-pointer transition-colors ${
                isCalc ? 'bg-emerald-50/30' : isIncluded ? 'bg-mld-admin-primary-50/30' : 'hover:bg-mld-menu-background'
            }`}
            onClick={() => !isCalc && onToggle(assay.code)}
        >
            <td className="px-6 py-2">
                {isCalc ? (
                    <span className="inline-block h-4 w-4 text-center text-emerald-500 text-xs">&#10003;</span>
                ) : (
                    <input
                        type="checkbox"
                        checked={isIncluded}
                        onChange={() => onToggle(assay.code)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-mld-panel-border text-mld-admin-primary focus:ring-mld-admin-primary"
                    />
                )}
            </td>
            <td className="px-3 py-2 font-medium text-mld-text-primary">
                {assay.name}
                {isCalc && <span className="ml-2 text-[10px] text-emerald-600">(calculated)</span>}
            </td>
            <td className="px-3 py-2 text-center text-mld-muted-text">{classLabel}</td>
            <td className="px-3 py-2 text-right font-mono text-xs text-mld-text-secondary">
                {isCalc ? freeBadge : fmt(testCost)}
            </td>
            <td className="px-3 py-2 text-right font-mono text-xs">
                {isCalc ? freeBadge : (
                    <span className={isIncluded ? 'text-mld-admin-primary font-medium' : 'text-mld-muted-text'}>
                        ${fmt(testPrice)}
                    </span>
                )}
            </td>
            <td className="px-3 py-2 text-right font-mono text-xs text-mld-text-secondary">
                {isCalc ? freeBadge : `$${fmt(addon)}`}
            </td>
        </tr>
    );
}
